import { computed, inject, Injectable, signal } from '@angular/core'
import { MAP } from '@bases/base.token'
import { HttpService } from '@bases/http.service'
import { API_URL } from '@core/constants/constant.data'
import { GEOJSON_FORMAT, RASTER_FORMAT, VECTOR_FORMAT } from '@core/constants/layers.data'
import { CMSession, CMSessionResponse, ResultLayerCM } from '@core/models/calculation-module.model'
import { backendResponse } from '@core/models/http.model'
import { isNullOrUndefinedString } from '@core/services/core.utilities'
import L from 'leaflet'
import { BehaviorSubject } from 'rxjs'
import { CMLayersService } from './cm-layers.service'

@Injectable({
  providedIn: 'root',
})
export class CMSessionService extends HttpService {
  private readonly _map = inject<BehaviorSubject<L.DrawMap>>(MAP)
  private readonly _cmLayerService = inject(CMLayersService)

  protected _baseUrl = API_URL + '/users/cm-sessions'

  cmSessions$ = signal<CMSession[]>([])
  layersLoading$ = signal<Record<string, boolean>>({})

  activeCMSessions$ = computed(() => {
    return this.cmSessions$().filter(
      (session) => session.selected == 'partially' || session.selected == 'true',
    )
  })

  constructor() {
    super()
  }

  setLayerLoadingStatus(layerPath: string, isLoading: boolean) {
    this.layersLoading$.update((layers) => ({
      ...layers,
      [layerPath]: isLoading,
    }))
  }

  addNewCMSession(cmSession: CMSession) {
    // set raster layer.selected = 'true' and adds to map
    if (!isNullOrUndefinedString(cmSession.results.raster_layers)) {
      cmSession.results.raster_layers?.map((layer) => {
        this.enableLayerCMSession(cmSession, layer, RASTER_FORMAT, false)
      })
    }

    // set vector layer.selected = 'true' and adds to map
    if (!isNullOrUndefinedString(cmSession.results.vector_layers)) {
      cmSession.results.vector_layers?.map((layer) => {
        this.enableLayerCMSession(cmSession, layer, VECTOR_FORMAT, false)
      })
    }

    if (!isNullOrUndefinedString(cmSession.results.geojson_layers)) {
      cmSession.results.geojson_layers?.map((layer) => {
        this.enableLayerCMSession(cmSession, layer, GEOJSON_FORMAT, false)
      })
    }

    // we add the cmSession to the SSOT
    this.cmSessions$.update((CMSessions) => {
      return [...CMSessions, cmSession]
    })
  }

  // to toggle on/off an existing cmSession (saved or not)
  enableCMSession(cmSession: CMSession) {
    const isSession = !isNullOrUndefinedString(cmSession.id) ? true : false
    const directoryUUID = cmSession.results?.directory_uuid || null

    if (!isNullOrUndefinedString(cmSession.results.raster_layers)) {
      cmSession.results.raster_layers?.map((raster) => {
        if (raster.selected !== true && directoryUUID) {
          this.enableLayerCMSession(cmSession, raster, RASTER_FORMAT, isSession, directoryUUID)
        }
      })
    }

    if (!isNullOrUndefinedString(cmSession.results.vector_layers)) {
      cmSession.results.vector_layers?.map((vector) => {
        if (vector.selected !== true && directoryUUID) {
          this.enableLayerCMSession(cmSession, vector, VECTOR_FORMAT, isSession, directoryUUID)
        }
      })
    }

    if (!isNullOrUndefinedString(cmSession.results.geojson_layers)) {
      cmSession.results.geojson_layers?.map((geojson) => {
        if (geojson.selected !== true && directoryUUID) {
          this.enableLayerCMSession(cmSession, geojson, GEOJSON_FORMAT, isSession, directoryUUID)
        }
      })
    }

    this._updateCMSessionStatusInSSOT(cmSession) //@TODO: can be improve because already call in each enableLayerCMSession
  }

  disableCMSession(cmSession: CMSession) {
    if (!isNullOrUndefinedString(cmSession.results.raster_layers)) {
      cmSession.results.raster_layers?.map((layer) => {
        this.disableCMSessionLayer(cmSession, layer, RASTER_FORMAT)
      })
    }

    if (!isNullOrUndefinedString(cmSession.results.vector_layers)) {
      cmSession.results.vector_layers?.map((layer) => {
        this.disableCMSessionLayer(cmSession, layer, VECTOR_FORMAT)
      })
    }

    if (!isNullOrUndefinedString(cmSession.results.geojson_layers)) {
      cmSession.results.geojson_layers?.map((layer) => {
        this.disableCMSessionLayer(cmSession, layer, GEOJSON_FORMAT)
      })
    }

    this._updateCMSessionStatusInSSOT(cmSession) //@TODO: can be improve because already call in each disableCMSessionLayer
  }

  enableLayerCMSession(
    cmSession: CMSession,
    layer: ResultLayerCM,
    layerFormat: string,
    isSession: boolean,
    pathForCMSession?: string,
  ) {
    layer.selected = true
    const addLayerPromise = this._cmLayerService.addLayerCM(
      layer.path,
      layerFormat,
      layer.layer,
      isSession,
      pathForCMSession,
    )
    this._updateCMSessionStatusInSSOT(cmSession)
    return addLayerPromise
  }

  disableCMSessionLayer(cmSession: CMSession, layer: ResultLayerCM, layerFormat: string) {
    if (layer.selected != true) return // can be false or not defined
    layer.selected = false
    this._cmLayerService.removeLayerFromMap(layer.path, layerFormat)
    this._updateCMSessionStatusInSSOT(cmSession)
  }

  private _updateCMSessionStatusInSSOT(cmSession: CMSession) {
    const newSelected = this._getCMSessionSelectionStatus(cmSession)

    this.cmSessions$.update((cmSessions) => {
      return cmSessions.map((session) => {
        if (session !== cmSession) {
          return session
        }
        session.selected = newSelected
        return session
      })
    })
  }

  private _getCMSessionSelectionStatus(cmSession: CMSession): 'true' | 'false' | 'partially' {
    const rasterLayers = cmSession.results?.raster_layers || []
    const vectorLayers = cmSession.results?.vector_layers || []
    const geojsonLayers = cmSession.results?.geojson_layers || []
    const allLayers = [...rasterLayers, ...vectorLayers, ...geojsonLayers]

    const allSelected = allLayers.every((l) => l.selected === true)
    const noneSelected = allLayers.every((l) => !l.selected)

    // CM with no layers
    if (allLayers.length == 0) {
      if (cmSession.selected == 'true') {
        return 'false'
      } else {
        return 'true'
      }
    }

    if (allSelected) {
      return 'true'
    }
    if (noneSelected) {
      return 'false'
    }
    return 'partially'
  }

  async saveSession(cmSession: CMSession) {
    const response = await this.addCMSessionAccount(cmSession) // Save Session with selected
    if (response === null) return
    const responseGet = await this.GETobject(response.id) // Get the new session from DB
    if (responseGet === null) return

    const cmSessionCopy = { ...cmSession } // To be sure to not edit the signal passed
    cmSessionCopy.id = responseGet.id
    cmSessionCopy.saved_at = responseGet.saved_at
    cmSessionCopy.results.directory_uuid = responseGet.results.directory_uuid

    // Update SSOT
    this.cmSessions$.update((cmSessions) => {
      return cmSessions.map((session) => {
        if (session != cmSession) {
          return session
        }
        return cmSessionCopy
      })
    })
  }

  async deleteCMSession(cmSessionToDelete: CMSession) {
    // Hide result
    if (cmSessionToDelete.selected != 'false') {
      this.disableCMSession(cmSessionToDelete)
    }
    if (cmSessionToDelete.id) {
      // Delete from account
      const response = this.deleteCMSessionAccount(cmSessionToDelete.id)
      if (response === null) return
    }

    // Remove from the list SSOT
    this.cmSessions$.update((cmSessions) => {
      return cmSessions.filter((cmSession) => cmSession !== cmSessionToDelete)
    })
  }

  addCMSessionAccount(cmSession: CMSession): Promise<backendResponse> {
    const CMSessionToSave = this._prepareCMSessionDataToSave(cmSession)
    return this.POST(CMSessionToSave)
  }

  // remove parameter use only in frontend
  private _prepareCMSessionDataToSave(cmSession: CMSession) {
    const cmResult = cmSession.results
    let rasterLayers: ResultLayerCM[] = []
    let vectorLayers: ResultLayerCM[] = []
    let geojsonLayers: ResultLayerCM[] = []

    if (cmResult.raster_layers) {
      rasterLayers = cmResult.raster_layers.map(({ name, path: path, type, symbology, layer }) => ({
        name,
        path,
        type,
        symbology,
        layer,
      }))
    }
    if (cmResult.vector_layers) {
      vectorLayers = cmResult.vector_layers.map(({ name, path: path, type, symbology, layer }) => ({
        name,
        path,
        type,
        symbology,
        layer,
      }))
    }
    if (cmResult.geojson_layers) {
      geojsonLayers = cmResult.geojson_layers.map(
        ({ name, path: path, type, symbology, layer }) => ({
          name,
          path,
          type,
          symbology,
          layer,
        }),
      )
    }

    const resultRequest = {
      name: cmResult.name,
      indicators: cmResult.indicators,
      graphics: cmResult.graphics,
      raster_layers: rasterLayers,
      vector_layers: vectorLayers,
      geojson_layers: geojsonLayers,
      csv_files: cmResult.csv_files,
      extra_zip_files: cmResult.extra_zip_files,
    }
    const saveRequest: CMSession = {
      session_name: cmSession.session_name,
      cm_id: cmSession.cm_id,
      payload: cmSession.payload,
      results: resultRequest,
    }
    return saveRequest
  }

  deleteCMSessionAccount(id: number): Promise<backendResponse> {
    return this.DELETE(id)
  }

  // Not used now, but keep for the day of cm session sharing
  updateCMSessionAccount(id: number, updates: Partial<CMSession>): Promise<backendResponse> {
    return this.PUT(id, updates)
  }

  isNumber(value: string) {
    return value.match(/^[-.0-9]+$/) != null
  }

  async exportCMresultFile(
    path: string,
    format: string,
    session_folder: string = '', // session_folder is defined only in case of saved session
  ): Promise<Blob> {
    const CMlayerToExport = {
      uuid: path,
      type: format,
      session_folder,
    }

    const url = API_URL + '/upload/export/cmLayer'

    return await this.POSTtoGetBlob(CMlayerToExport, url)
  }

  defineExtensionLayer(format: string) {
    // In order to use the CM session name as file name, must check type
    // the extension can be also found with data.name
    let extension = ''
    if (format == RASTER_FORMAT) {
      extension = '.tif'
    } else if (format == VECTOR_FORMAT) {
      extension = '.zip'
    } else if (format == GEOJSON_FORMAT) {
      extension = '.geojson'
    } else {
      console.warn('This type is not known ', format)
      this._toasterService.openSnackBarError('This layer type is not known')
      return
    }
    return extension
  }

  async centerMap(response: CMSessionResponse) {
    if (response.bounds) {
      const [minLng, minLat, maxLng, maxLat] = response.bounds
      const leafletBounds = L.latLngBounds([minLat, minLng], [maxLat, maxLng])
      this._map.getValue().fitBounds(leafletBounds)
    } else if (response.center) {
      const center = L.latLng(response.center.lat, response.center.lng)
      this._map.getValue().setView(center, 12)
    } else {
      return
    }
  }

  async cmLayerCenterMap(cmLayer: ResultLayerCM) {
    if (!cmLayer || !cmLayer.path) {
      return null
    }

    const url = `${API_URL}/users/cm-layers-bounds/${cmLayer.path}`

    const response = await this.GETobject(cmLayer.path, url)
    if (!response) {
      return null
    }

    this.centerMap(response)
  }
}
